import { PropGraph } from "./PropGraph";

export type PropSimulationIaaProfileKey =
  | "free"
  | "payOnce"
  | "payThree"
  | "optimal";

export interface PropSimulationIaaProfile {
  key: PropSimulationIaaProfileKey;
  name: string;
  description: string;
  percent: number;
}

export interface PropSimulationConfig {
  games: number;
  seed: number;
  frameGameBudget: number;
  gameIndexOffset?: number;
  performanceSampleRate?: number;
  iaaProfiles: PropSimulationIaaProfile[];
}

export interface PropSimulationPerformanceMetric {
  stage: string;
  label: string;
  totalMs: number;
  averageMs: number;
  percent: number;
  samples: number;
}

export interface PropSimulationRunSummary {
  games: number;
  totalTurns: number;
  totalMagic: number;
  averageMagicPerGame: number;
  averageMagicPerTurn: number;
  averageLibrarySize?: number;
  maxLibrarySize?: number;
  prunedMaterialCount?: number;
  commissionAverageMagic: number[];
  propCount: number;
  positivePropCount: number;
  negativePropCount: number;
  presentPropCount: number;
  neverPresentPropCount: number;
  elapsedMs: number;
  performance?: PropSimulationPerformanceMetric[];
}

export interface PropSimulationProfileSummary {
  key: PropSimulationIaaProfileKey;
  name: string;
  games: number;
  totalMagic: number;
  averageMagicPerGame: number;
  averageMagicPerTurn: number;
}

export interface PropSimulationTypeSummary {
  type: number;
  typeName: string;
  rarity: number;
  rarityName: string;
  count: number;
  averageGlobalStrength: number;
  averagePresentStrength: number;
}

export interface PropSimulationValueComponents {
  direct: number;
  directMana: number;
  stateDelta: number;
  growth: number;
  growDelta: number;
  condition: number;
  spawn: number;
  spawnDelta: number;
  eliminate: number;
  eliminateDelta: number;
  transform: number;
  transformDelta: number;
  defense: number;
  protectDelta: number;
  purifyDelta: number;
  accelerateDelta: number;
  weight: number;
  rarityWeightDelta: number;
  pool: number;
  synergy: number;
  cost: number;
}

export interface PropSimulationStatRow {
  id: string;
  name: string;
  assetId: string;
  type: number;
  typeName: string;
  rarity: number;
  rarityName: string;
  tags: number[];
  tagNames: string[];
  derived: boolean;
  contribution: number;
  globalStrength: number;
  presentStrength: number;
  sourceContribution?: number;
  sourceGlobalStrength?: number;
  sourcePresentStrength?: number;
  intrinsicContribution?: number;
  intrinsicGlobalStrength?: number;
  intrinsicPresentStrength?: number;
  bodyContribution?: number;
  bodyGlobalStrength?: number;
  bodyPresentStrength?: number;
  finalValue?: number;
  balanceValue?: number;
  actualCalculatedStrength?: number;
  balancedSimulationStrength?: number;
  strengthCoefficient?: number | null;
  propUpdatedAt?: number;
  triggerValue?: number;
  poolValue?: number;
  synergyValue?: number;
  staticPrior?: number;
  expectedValue?: number;
  deviation?: number;
  deviationRate?: number;
  confidenceLow?: number;
  confidenceHigh?: number;
  confidenceRadius?: number;
  sampleCount?: number;
  effectiveSampleCount?: number;
  components?: Partial<PropSimulationValueComponents>;
  presentRounds: number;
  uses: number;
  effectEvents?: number;
}

export type PropSimulationStatScopeType =
  | "all"
  | "commission"
  | "round"
  | "system"
  | "profile"
  | "filter";

export interface PropSimulationStatScope {
  key: string;
  type: PropSimulationStatScopeType;
  label: string;
  rows: PropSimulationStatRow[];
  summary?: PropSimulationRunSummary;
  meta?: {
    commissionIndex?: number;
    roundIndex?: number;
    profileKey?: PropSimulationIaaProfileKey;
    systemTag?: number;
  };
}

export interface PropSimulationResult {
  _id?: string;
  storageVersion?: number;
  config: PropSimulationConfig;
  strategyText: string[];
  summary: PropSimulationRunSummary;
  profileSummaries: PropSimulationProfileSummary[];
  typeSummaries: PropSimulationTypeSummary[];
  rows: PropSimulationStatRow[];
  statScopes?: PropSimulationStatScope[];
}

export interface PropSimulationHistory {
  _id: string;
  name: string;
  createdAt: number;
  storageVersion?: number;
  config: PropSimulationConfig;
  strategyText: string[];
  summary: PropSimulationRunSummary;
  profileSummaries: PropSimulationProfileSummary[];
  typeSummaries: PropSimulationTypeSummary[];
  rows?: PropSimulationStatRow[];
  statScopes?: PropSimulationStatScope[];
}

export type PropSimulationDictItem = {
  value: number | string;
  label?: string;
};

export interface PropSimulationInputData {
  props: PropGraph[];
  tags: PropSimulationDictItem[];
  types: PropSimulationDictItem[];
  rarities: PropSimulationDictItem[];
  strengths: Array<Record<string, any>>;
}

export const DEFAULT_PROP_SIMULATION_CONFIG: PropSimulationConfig = {
  games: 1000000,
  seed: 20260609,
  frameGameBudget: 12,
  gameIndexOffset: 0,
  performanceSampleRate: 50,
  iaaProfiles: [
    {
      key: "free",
      name: "完全不付费",
      description: "只接受每轮免费的原料三选一，不购买辅料，不刷新候选项。",
      percent: 40,
    },
    {
      key: "payOnce",
      name: "平均每轮付费1次",
      description: "优先购买高价值辅料；辅料已满后，将付费次数用于刷新原料或配方选择。",
      percent: 30,
    },
    {
      key: "payThree",
      name: "平均每轮付费3次",
      description: "每轮购买辅料并进行有限刷新，在候选项中选择评分最高的道具。",
      percent: 20,
    },
    {
      key: "optimal",
      name: "无限付费直到最优",
      description: "将可出现候选池视为可充分刷新，直接选择当前委托下评分最高的道具。",
      percent: 10,
    },
  ],
};
